<template>
  <div class="page">
    <!-- OVERLAY FOR RESULTS UPDATE -->
    <v-overlay
      absolute
      :value="isUpdateQuoteShow && $vuetify.breakpoint.lgAndUp"
    />

    <!-- HEADER -->
    <site-header
      ref="header"
      :partner-logo="config.partnerImage"
      :partner-text="config.partnerText"
      :partner-header-color="config.partnerHeaderColor"
      :partner-exit-url="config.partnerExitUrl"
      :header-height="config.headerHeight"
    />

    <!-- SMALL DEVICES ADJUST OPTIONS -->
    <v-sheet
      v-if="$vuetify.breakpoint.mdAndDown"
      color="navy-medium"
      dark
      tile
      class="px-3 d-flex align-center"
    >
      <v-col>
        <v-btn
          :class="{'invisible': isQuotesLoading}"
          outlined
          rounded
          small
          color="white"
          data-test="adjust_your_cover"
          @click="isOptionsDrawerVisible = !isOptionsDrawerVisible"
        >
          <v-icon
            small
            class="mr-1"
          >
            mdi-tune
          </v-icon>
          Adjust your cover
        </v-btn>
      </v-col>
    </v-sheet>

    <!-- RESULTS TABLE / RESULTS FILTERS -->
    <v-container
      v-if="hasSucceededQuotes || !isQuotesLoading"
      :fluid="$vuetify.breakpoint.lgAndDown"
      class="py-4 px-md-8"
    >
      <v-row class="my-0">
        <!-- RESULTS FILTERS -->
        <v-col
          cols="12"
          lg="3"
          class="py-0"
        >
          <v-navigation-drawer
            v-model="isOptionsDrawerVisible"
            :app="$vuetify.breakpoint.mdAndDown"
            :right="$vuetify.breakpoint.mdAndDown"
            align-self="end"
            clipped
            floating
            touchless
            :color="$vuetify.breakpoint.lgAndUp ? 'transparent' : undefined"
            :width="$vuetify.breakpoint.lgAndUp ? 400 : 360"
            :height="$vuetify.breakpoint.lgAndUp ? 'auto' : '100%'"
            :permanent="$vuetify.breakpoint.lgAndUp"
            :style="$vuetify.breakpoint.lgAndUp && `position:sticky; top: ${fixedTopIndent}px;`"
            :class="[ $vuetify.breakpoint.lgAndUp && 'fix-height',
                      isQuotesLoading && 'v-navigation-drawer-disabled',
                      isUpdateQuoteShow && 'requote-shown' ]"
          >
            <results-options
              v-if="formData"
              :form-data="formData"
              :details-description="getInitDetailsDescription"
              :options="filterOptions"
              :selected-filters="selectedFilters"
              :is-update-quote-show="isUpdateQuoteShow"
              :travel-dropdowns="travelDropdowns"
              @click-edit-quote="goToForm"
              @click-close="isOptionsDrawerVisible = !isOptionsDrawerVisible"
              @click-details="openDetailsDrawer"
              @change-requote-filter="handleChangeRequoteFilter"
              @change-filter="handleChangeFilter"
              @cancel-re-quote="cancelReQuote"
              @re-quote="reQuote"
              @update-results="updateTravelResults"
              @clear-filters="clearTravelFilters"
            />
          </v-navigation-drawer>
        </v-col>

        <!-- RESULTS TABLE -->
        <v-col
          cols="12"
          lg="9"
          class="py-0 d-flex flex-column flex-grow-1"
        >
          <!-- Medical Conditions-->
          <medical-conditions-info class="mb-4" />

          <main
            class="flex-grow-1"
          >
            <results-table
              :quote-ref="quoteRef"
              :quotes-qty="quotesQuantity"
              :columns="resultsTableColumns"
              :insured-name="insuredName"
              :all-results-qty="allResults.length"
              :failed-quotes="failedResults"
              :is-no-results="isNoResults"
              :has-quote-summary="!!quoteSummary"
              :selected-optional-extras="selectedOptionalExtras"
              :fixed-top-indent="fixedTopIndent"
              :has-broker-results="hasBrokerResults"
              :is-broker-solution-message-shown="isBrokerSolutionMessageShown"
              :is-broker-solution-table-shown="isBrokerSolutionTableShown"
              :broker-quotes-qty="brokerShownResults.length"
              @open-drawer-details="openDetailsDrawer"
              @go-to-form="goToForm"
            >
              <!-- BROKER NOTIFICATIONS -->
              <template
                v-if="isBrokerSolutionMessageShown"
                #brokerResultsMessage
              >
                <results-notification
                  :partner-text="config.partnerText"
                  :insurer-name="insuredName"
                  :policy-type="policyType"
                  :trip-destination="tripDestination"
                  :policy-start-date="policyStartDate"
                  :policy-end-date="policyEndDate"
                />
              </template>
              <!-- BROKER CUSTOM MESSAGE -->
              <template
                v-if="config.partnerMessage"
                #partnerMessage
              >
                <div v-html="config.partnerMessage" />
              </template>
              <!-- BROKER RESULTS -->
              <template
                v-if="isBrokerSolutionTableShown"
                #brokerResults
              >
                <div
                  v-for="(chosenQuote, index) of brokerShownResults"
                  :id="'chosen-quote-'+chosenQuote.id"
                  :key="'chosen-quote-'+chosenQuote.id"
                  class="quote-container mb-6"
                >
                  <results-quote
                    :quote="chosenQuote"
                    :quotes-loading="isQuotesLoading"
                    :is-view-monthly="isViewMonthly"
                    :optional-extras-columns="getOptionalExtrasValues"
                    :selected-optional-extras="optionalExtras"
                    :cover-boost-products="coverBoostProducts"
                    @click-view-details="handleClickViewDetailsResultQuote($event, index)"
                    @open-drawer-details="openDetailsDrawer"
                    @toggle-quote-shown="toggleQuoteShown($event, brokerResults)"
                  />
                </div>
              </template>
              <!-- QZ NOTIFICATION -->
              <template
                v-if="isQZShownMessageShown"
                #QZMessage
              >
                <results-broker-notification
                  v-if="isBrokerSolutionEnabled"
                  :has-broker-results="hasBrokerResults"
                  :quotes-qty="quotesQuantity"
                  :shown-results-cheapest-price="shownResultsCheapestPrice"
                />
                <results-notification
                  v-else
                  :insurer-name="insuredName"
                  :policy-type="policyType"
                  :trip-destination="tripDestination"
                  :policy-start-date="policyStartDate"
                  :policy-end-date="policyEndDate"
                />
              </template>
              <!-- ALL RESULTS -->
              <template #quotes>
                <div
                  v-if="isQZShown"
                  key="quotes"
                >
                  <div
                    v-for="(quote, index) of shownResults"
                    :key="quote.id"
                    class="quote-container mb-6"
                  >
                    <transition name="fade-in">
                      <results-quote
                        v-show="quote.isVisible"
                        :quote="quote"
                        :is-view-monthly="isViewMonthly"
                        :optional-extras-columns="getOptionalExtrasValues"
                        :selected-optional-extras="optionalExtras"
                        :cover-boost-products="coverBoostProducts"
                        :quotes-loading="isQuotesLoading"
                        @click-view-details="handleClickViewDetailsResultQuote($event, index)"
                        @open-drawer-details="openDetailsDrawer"
                        @toggle-quote-shown="toggleQuoteShown"
                      />
                    </transition>
                  </div>
                </div>
              </template>
            </results-table>
            <results-additional-quotes
              v-if="isAdditionalResultsCTAShown"
              @get-more-quotes="onBrokerSolutionModalSuccess"
            />
          </main>
        </v-col>
      </v-row>

      <!-- FOOTER -->
      <v-footer
        v-if="config.partnerFooter"
        padless
        color="transparent"
        tag="div"
      >
        <site-footer role="footer">
          <div v-html="config.partnerFooterText || ''" />
        </site-footer>
      </v-footer>

      <div class="text-center">
        <branding-logo
          v-if="config.brandingLogo"
          :branding-type="config.brandingType"
        />
      </div>

      <!-- RESULTS FOUND SMALL MODAL -->
      <loading-notice
        :is-only-loading-shown="isBrokerSolutionEnabled"
        :is-loading="isQuotesLoading"
        :totalcount="quotesQuantity"
      />
    </v-container>

    <!-- ASIDE DRAWER -->
    <v-navigation-drawer
      v-model="isDetailsDrawerOpen"
      app
      right
      temporary
      touchless
      :width="drawerWidth"
      height="100%"
    >
      <keep-alive>
        <component
          :is="drawerComponent"
          ref="drawer"
          :quote="detailsQuote"
          :cover-boost-products="coverBoostProducts"
          :quote-position-number="quotePositionNumber"
          :details-description="getDetailsDescription"
          :show-monthly="false"
          :is-details-drawer-open="isDetailsDrawerOpen"
          :selected-optional-extras="optionalExtras"
          :quote-summary="quoteSummary"
          :policy-type="policyType"
          :form-step-labels="formStepLabels"
          :product-name="productName"
          :is-desktop="isDesktop"
          :name="insuredName"
          :selected-premium="selectedPremium"
          @open-drawer-details="openDetailsDrawer"
          @go-to-form="goToForm"
          @click-view-details="handleClickViewDetails($event)"
          @close-drawer="closeDetailsDrawer"
        />
      </keep-alive>
    </v-navigation-drawer>
    <!-- BROKER SOLUTION EXIT ONLY MODAL -->
    <results-modal-alerts
      v-if="isBrokerSolutionModalShowed"
      ref="brokerModal"
      modal-alert="brokerModal"
      @success="onBrokerSolutionModalSuccess"
      @cancel="onBrokerSolutionModalCancel"
    />

    <!-- RESULTS FAILED MODAL -->
    <results-modal-alerts
      :modal-alert="modalAlert"
      @go-to-form="goToForm"
      @cancel="modalAlert = false"
    />

    <!-- RESULTS LOADING MODAL -->
    <results-loading-modal
      :quoting-done-percentage="quotingDonePercentage"
      :has-succeeded-quotes="hasSucceededQuotes"
      :is-quotes-loading="isQuotesLoading"
    />
  </div>
</template>

<script>
import ResultsDetails from '@/components/travel/Results/ResultsDetails';
import ResultsOptions from '@/components/travel/Results/ResultsOptions';
import CoverBoostOptions from '@/components/CoverBoost/CoverBoostOptions';
import ResultsTable from '@/components/car/Results/ResultsTable';
import DetailsQuoteSummary from '@/components/car/Results/detailsDrawer/DetailsQuoteSummary';
import DetailsDescription from '@/components/car/Results/detailsDrawer/DetailsDescription';
import ResultsModalAlerts from '@/components/car/Results/ResultsModalAlerts';
import LoadingNotice from '@/components/car/Results/ResultsLoadingNotice';
import ResultsBrokerNotification from '@/components/car/Results/ResultsBrokerNotification';
import ResultsAdditionalQuotes from '@/components/car/Results/ResultsAdditionalQuotes';
import ResultsQuote from '@/components/travel/Results/ResultsQuote';
import ResultsNotification from '@/components/travel/Results/ResultsNotification';
import { travelStructure } from '@/helpers/products/travel';
import useQuotezoneCommon from '@/composables/useQuotezoneCommon';
import useQuotezoneResult from '@/composables/useQuotezoneResult';
import useBrokerSolution from '@/composables/useBrokerSolution';
import useCoverBoost from '@/composables/useCoverBoost';
import detailsDescription from '@/helpers/travel/details.json';
import MedicalConditionsInfo from '@/components/shared/MedicalConditionsInfo';
import { ref } from '@vue/composition-api';
import ResultsLoadingModal from '@/components/ResultsLoadingModal';

export default {
  name: 'page-travel-insurance-results',
  provide: ['productName'],
  inject: ['config', 'constants'],
  metaInfo () {
    return {
      title: `${this.config.partnerText} | Travel Insurance | Quotes`,
    };
  },
  components: {
    CoverBoostOptions,
    MedicalConditionsInfo,
    ResultsDetails,
    ResultsOptions,
    ResultsTable,
    DetailsQuoteSummary,
    DetailsDescription,
    ResultsModalAlerts,
    LoadingNotice,
    ResultsQuote,
    ResultsLoadingModal,
    ResultsNotification,
    ResultsBrokerNotification,
    ResultsAdditionalQuotes,
  },
  data () {
    return {
      // Pass to DetailsList, based on data get step number
      // when click edit details
      formStepLabels: {
        'Your Trip': '1',
        'Traveller Details': '2',
      },
    };
  },
  computed: {
    isDesktop () {
      return this.$vuetify.breakpoint.width >= 768;
    },
    tripDestination () {
      return this.quoteSummary && Object.keys(this.quoteSummary).length
        ? this.quoteSummary['Your Trip'].Destination
        : '';
    },
    policyType () {
      return this.quoteSummary && Object.keys(this.quoteSummary).length
        ? this.quoteSummary['Your Trip']['Type of cover']
        : '';
    },
    tripFormData () {
      return this.formData?.trip;
    },
    policyStartDate () {
      return this.tripFormData?.startDate || this.tripFormData?.startDateExpired;
    },
    policyEndDate () {
      return this.tripFormData?.endDate || this.tripFormData?.endDateExpired;
    },
    resultsTableColumns () {
      return [
        {
          name: 'Provider',
        },
        {
          name: 'Price',
        },
        {
          name: 'Max excess per feature',
          message: 'This is the maximum amount you have to pay per person per claim.',
        },
        {
          name: 'Medical cover',
          message: 'The level of medical cover shown is per person for emergency medical expenses',
        },
        {
          name: 'Cancellation cover',
          message: 'If the holiday is cancelled this is the maximum amount you can claim per person for unrecoverable costs',
        },
        {
          name: 'Baggage cover',
          message: 'This is the maximum amount per person that can be claimed if your baggage is accidentally damaged, lost or stolen',
        },
        {
          name: 'Policy details',
        },
      ];
    },
    filterOptions () {
      return {
        optionalExtrasOptions: this.constants.OPTIONAL_EXTRAS_OPTIONS,
        additionalCoverOptions: this.constants.ADDITIONAL_COVER_OPTIONS,
      };
    },
    selectedFilters () {
      return {
        isViewMonthly: this.isViewMonthly,
        optionalExtras: this.optionalExtras,
        travelDropdowns: this.travelDropdowns,
      };
    },
    getOptionalExtrasValues () {
      return this.filterOptions.optionalExtrasOptions.map(option =>
        option.value);
    },
  },
  async created () {
    await this.getQuotes(true);
    this.clearLocalStorage();
    this.initSelectedFilters();
    if (this.isBrokerSolutionEnabled) {
      this.initExitOnlyModal();
    }
  },
  methods: {
    handleChangeFilter ({ filter, value }) {
      this.changeFilter({ filter, value }, this.selectedFilters);
    },
    handleChangeRequoteFilter ({ filter, val }) {
      if (this.formData.trip[filter] === val) return;
      this.formData.trip[filter] = val;
      this.isUpdateQuoteShow = true;
    },
    clearTravelFilters () {
      this.travelDropdowns.map(panel => {
        panel.selected = panel.options[0].value.key;
        panel.selectedValue = panel.options[0].value;
      });

      const selectedOptions = this.$ls.get(this.productName + '__selected-options');

      if (!selectedOptions) return;

      delete selectedOptions.value.travelDropdowns;

      this.$ls.set(this.productName + '__selected-options', selectedOptions);
    },
    updateTravelResults ({ type, value }) {
      const currentFilter = this.travelDropdowns.find(panel => panel.type === type);

      if (!currentFilter) {
        return false;
      }

      currentFilter.selected = value.key;

      const selectedFilters = this.$ls.get(this.productName + '__selected-options') || this.$ls.get('selected-options');

      this.$ls.set(this.productName + '__selected-options', {
        ...selectedFilters,
        travelDropdowns: this.travelDropdowns,
      });
    },
  },
  setup (props, context) {
    const productName = 'travel';
    const travelDropdowns = ref([
      {
        header: 'Maximum Excess',
        type: 'maxExcess',
        selected: 'any_excess',
        options: [
          {
            text: 'Any Excess',
            value: {
              target: 0,
              rule: 'above',
              key: 'any_excess',
            },
          },
          {
            text: 'Up to £250',
            value: {
              target: 250,
              rule: 'below',
              key: 'up_to_250',
            },
          },
          {
            text: 'Up to £200',
            value: {
              target: 200,
              rule: 'below',
              key: 'up_to_200',
            },
          },
          {
            text: 'Up to £150',
            value: {
              target: 150,
              rule: 'below',
              key: 'up_to_150',
            },
          },
          {
            text: 'Up to £100',
            value: {
              target: 100,
              rule: 'below',
              key: 'up_to_100',
            },
          },
          {
            text: 'Up to £50',
            value: {
              target: 50,
              rule: 'below',
              key: 'up_to_50',
            },
          },
          {
            text: 'No Excess',
            value: {
              target: 0,
              rule: 'exactly',
              key: 'no_excess',
            },
          },
        ],
        get selectedValue () {
          return this.options.find(option => option.value.key === this.selected).value;
        },
      },
      {
        header: 'Medical Cover',
        type: 'medical',
        selected: 'any',
        options: [
          {
            text: 'Any',
            value: {
              target: 0,
              rule: 'above',
              key: 'any',
            },
          },
          {
            text: 'At least £2m',
            value: {
              target: 2000000,
              rule: 'above',
              key: 'at_least_2m',
            },
          },
          {
            text: 'At least £5m',
            value: {
              target: 5000000,
              rule: 'above',
              key: 'at_least_5m',
            },
          },
          {
            text: 'At least £10m',
            value: {
              target: 10000000,
              rule: 'above',
              key: 'at_least_10m',
            },
          },
          {
            text: 'At least £15m',
            value: {
              target: 15000000,
              rule: 'above',
              key: 'at_least_15m',
            },
          },
          {
            text: 'At least £20m',
            value: {
              target: 20000000,
              rule: 'above',
              key: 'at_least_20m',
            },
          },
        ],
        get selectedValue () {
          return this.options.find(option => option.value.key === this.selected).value;
        },
      },
      {
        header: 'Cancellation Cover',
        type: 'cancellation',
        selected: 'any',
        options: [
          {
            text: 'Any',
            value: {
              target: 0,
              rule: 'above',
              key: 'any',
            },
          },
          {
            text: 'At least £750',
            value: {
              target: 750,
              rule: 'above',
              key: 'at_least_750',
            },
          },
          {
            text: 'At least £1,000',
            value: {
              target: 1000,
              rule: 'above',
              key: 'at_least_1000',
            },
          },
          {
            text: 'At least £2,000',
            value: {
              target: 2000,
              rule: 'above',
              key: 'at_least_2000',
            },
          },
          {
            text: 'At least £3,000',
            value: {
              target: 3000,
              rule: 'above',
              key: 'at_least_3000',
            },
          },
          {
            text: 'At least £4,000',
            value: {
              target: 4000,
              rule: 'above',
              key: 'at_least_4000',
            },
          },
          {
            text: 'At least £5,000',
            value: {
              target: 5000,
              rule: 'above',
              key: 'at_least_5000',
            },
          },
        ],
        get selectedValue () {
          return this.options.find(option => option.value.key === this.selected).value;
        },
      },
      {
        header: 'Baggage Cover',
        type: 'baggage',
        selected: 'any',
        options: [
          {
            text: 'Any',
            value: {
              target: 0,
              rule: 'above',
              key: 'any',
            },
          },
          {
            text: 'At least £500',
            value: {
              target: 500,
              rule: 'above',
              key: 'at_least_500',
            },
          },
          {
            text: 'At least £1,000',
            value: {
              target: 1000,
              rule: 'above',
              key: 'at_least_1000',
            },
          },
          {
            text: 'At least £2,000',
            value: {
              target: 2000,
              rule: 'above',
              key: 'at_least_2000',
            },
          },
          {
            text: 'At least £3,000',
            value: {
              target: 3000,
              rule: 'above',
              key: 'at_least_3000',
            },
          },
        ],
        get selectedValue () {
          return this.options.find(option => option.value.key === this.selected).value;
        },
      },
    ]);
    const stepIds = travelStructure.map(step => step.stepId);
    const common = useQuotezoneCommon({ productName }, context);
    const result = useQuotezoneResult({
      stepIds,
      detailsDescription,
      productName,
      gtmTracking: common.gtmTracking,
      travelDropdowns,
    }, context);
    const coverBoost = useCoverBoost(result, context);
    const broker = useBrokerSolution(result, context);

    return {
      ...common,
      travelDropdowns,
      ...result,
      ...broker,
      ...coverBoost,
    };
  },
};
</script>

<style lang="scss" src='@/assets/scss/results/core-component.scss'></style>

<style lang="scss" scoped>
  .invisible {
    visibility: hidden;
  }
</style>
